import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/app/$propertyId/members')({
  component: PropertyMembers,
});

type Member = {
  userId: string;
  role: 'owner' | 'member';
  displayName: string;
  email: string;
  addedAt: string;
};

type PropertyDetail = { role: 'owner' | 'member' | null };

type PendingInvite = { id: string; email: string; expiresAt: string; createdAt: string };

type OwnershipTransfer = {
  id: string;
  propertyId: string;
  fromUserId: string;
  toUserId: string;
  state: 'pending' | 'accepted' | 'declined' | 'cancelled';
  initiatedAt: string;
  resolvedAt: string | null;
  expiresAt: string;
};

const INVITE_ERRORS: Record<string, string> = {
  invite_cap_reached: 'You have no invites remaining.',
  invite_already_pending: 'An invite is already pending for that email.',
  validation_failed: 'Email is required.',
};

const TRANSFER_ERRORS: Record<string, string> = {
  transfer_already_pending: 'A transfer is already pending for this property.',
  target_not_member: 'That user must be a member of this property first.',
};

function PropertyMembers() {
  const { propertyId } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: propData } = useQuery({
    queryKey: ['property', propertyId],
    queryFn: () =>
      api.get<{ property: PropertyDetail }>('/v1/properties/' + propertyId),
  });

  const isOwner = propData?.property?.role === 'owner';

  const { data: membersData, isPending } = useQuery({
    queryKey: ['members', propertyId],
    queryFn: () =>
      api
        .get<{ members: Member[] }>('/v1/properties/' + propertyId + '/members')
        .then((r) => r.members),
  });

  const members = membersData ?? [];

  // Current user identity
  const { data: meData } = useQuery({
    queryKey: ['me'],
    queryFn: () =>
      api
        .get<{ user: { id: string; displayName: string; email: string } }>('/v1/me')
        .then((r) => r.user),
  });

  const currentUserId = meData?.id;

  // Pending invites
  const { data: invitesData, refetch: refetchInvites } = useQuery({
    queryKey: ['invites', propertyId],
    queryFn: () =>
      api
        .get<{ invites: PendingInvite[] }>('/v1/properties/' + propertyId + '/invites')
        .then((r) => r.invites),
    enabled: isOwner,
  });

  const invites = invitesData ?? [];

  const cancelInviteMutation = useMutation({
    mutationFn: (inviteId: string) =>
      api.delete('/v1/properties/' + propertyId + '/invites/' + inviteId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['invites', propertyId] }),
  });

  // Ownership transfers
  const { data: transfersData } = useQuery({
    queryKey: ['ownership-transfers', propertyId],
    queryFn: () =>
      api
        .get<{ transfers: OwnershipTransfer[] }>(
          '/v1/properties/' + propertyId + '/ownership-transfers',
        )
        .then((r) => r.transfers),
  });

  const transfers = transfersData ?? [];

  const pendingOutgoing = transfers.find(
    (t) => t.state === 'pending' && t.fromUserId === currentUserId,
  );
  const pendingIncoming = transfers.find(
    (t) => t.state === 'pending' && t.toUserId === currentUserId,
  );

  // Transfer initiation
  const [transferTargetId, setTransferTargetId] = useState('');
  const [transferError, setTransferError] = useState<string | null>(null);

  const initiateTransferMutation = useMutation({
    mutationFn: (toUserId: string) =>
      api.post('/v1/properties/' + propertyId + '/ownership-transfers', { toUserId }),
    onSuccess: () => {
      setTransferTargetId('');
      setTransferError(null);
      void qc.invalidateQueries({ queryKey: ['ownership-transfers', propertyId] });
      void qc.invalidateQueries({ queryKey: ['members', propertyId] });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setTransferError(TRANSFER_ERRORS[err.code ?? ''] ?? err.message ?? 'Transfer failed.');
      } else {
        setTransferError('Transfer failed.');
      }
    },
  });

  const cancelTransferMutation = useMutation({
    mutationFn: (transferId: string) =>
      api.post('/v1/ownership-transfers/' + transferId + '/cancel'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ownership-transfers', propertyId] });
      void qc.invalidateQueries({ queryKey: ['members', propertyId] });
    },
  });

  // Accept transfer — two-phase: try without flag, retry with confirmAutoArchive on cap error
  const [autoArchiveWarn, setAutoArchiveWarn] = useState<{
    transferId: string;
    properties: string[];
  } | null>(null);

  const acceptTransferMutation = useMutation({
    mutationFn: ({
      transferId,
      confirmAutoArchive,
    }: {
      transferId: string;
      confirmAutoArchive?: boolean;
    }) =>
      api.post(
        '/v1/ownership-transfers/' + transferId + '/accept',
        confirmAutoArchive ? { confirmAutoArchive: true } : {},
      ),
    onSuccess: () => {
      setAutoArchiveWarn(null);
      void navigate({ to: '/app/dashboard' });
    },
    onError: (err, variables) => {
      if (err instanceof ApiError && err.code === 'transfer_over_cap') {
        const willAutoArchive = (err.details as { willAutoArchive: string[] })?.willAutoArchive;
        if (willAutoArchive?.length) {
          setAutoArchiveWarn({ transferId: variables.transferId, properties: willAutoArchive });
          return;
        }
      }
    },
  });

  const declineTransferMutation = useMutation({
    mutationFn: (transferId: string) =>
      api.post('/v1/ownership-transfers/' + transferId + '/decline'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ownership-transfers', propertyId] });
      void qc.invalidateQueries({ queryKey: ['members', propertyId] });
    },
  });

  // Invite
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);

  const inviteMutation = useMutation({
    mutationFn: () =>
      api.post('/v1/properties/' + propertyId + '/invites', {
        email: inviteEmail.trim(),
        role: 'member',
      }),
    onSuccess: () => {
      setInviteEmail('');
      setInviteError(null);
      setInviteSuccess(true);
      setTimeout(() => setInviteSuccess(false), 3000);
      void qc.invalidateQueries({ queryKey: ['invites', propertyId] });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setInviteError(INVITE_ERRORS[err.code ?? ''] ?? err.message ?? 'Invite failed.');
      } else {
        setInviteError('Invite failed.');
      }
    },
  });

  // Remove
  const removeMutation = useMutation({
    mutationFn: (userId: string) =>
      api.delete('/v1/properties/' + propertyId + '/members/' + userId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['members', propertyId] }),
  });

  const eligibleTransferTargets = members.filter((m) => m.role === 'member');

  return (
    <div className="p-6 max-w-2xl">
      <h2 className="text-lg font-semibold mb-4">Members</h2>

      {isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="rounded-md border border-border overflow-hidden mb-6">
          <table className="w-full text-sm">
            <thead className="bg-muted text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Name</th>
                <th className="text-left px-4 py-2 font-medium">Email</th>
                <th className="text-left px-4 py-2 font-medium">Role</th>
                <th className="text-left px-4 py-2 font-medium">Added</th>
                {isOwner && <th />}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {members.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-4 text-center text-muted-foreground">
                    No members yet.
                  </td>
                </tr>
              )}
              {members.map((m) => (
                <tr key={m.userId} className="hover:bg-muted/40">
                  <td className="px-4 py-2 font-medium">{m.displayName}</td>
                  <td className="px-4 py-2 text-muted-foreground">{m.email}</td>
                  <td className="px-4 py-2 capitalize">{m.role}</td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {new Date(m.addedAt).toLocaleDateString()}
                  </td>
                  {isOwner && (
                    <td className="px-4 py-2 text-right">
                      {m.role !== 'owner' && (
                        <button
                          className="text-xs text-destructive hover:underline disabled:opacity-50"
                          onClick={() => removeMutation.mutate(m.userId)}
                          disabled={removeMutation.isPending}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isOwner && (
        <div className="rounded-lg border border-border p-4 flex flex-col gap-3">
          <h3 className="font-medium text-sm">Invite a member</h3>
          <div className="flex gap-2">
            <input
              type="email"
              placeholder="Email address"
              value={inviteEmail}
              onChange={(e) => { setInviteEmail(e.target.value); setInviteError(null); }}
              className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <Button
              size="sm"
              onClick={() => inviteMutation.mutate()}
              disabled={!inviteEmail.trim() || inviteMutation.isPending}
            >
              {inviteMutation.isPending ? 'Sending…' : 'Invite'}
            </Button>
          </div>
          {inviteError && <p className="text-sm text-destructive">{inviteError}</p>}
          {inviteSuccess && <p className="text-sm text-green-600">Invite sent.</p>}
        </div>
      )}

      {isOwner && (
        <div className="mt-6">
          <h3 className="font-medium text-sm mb-3">Pending invites</h3>
          {invites.length === 0 ? (
            <p className="text-sm text-muted-foreground">No pending invites.</p>
          ) : (
            <div className="rounded-md border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Email</th>
                    <th className="text-left px-4 py-2 font-medium">Expires</th>
                    <th />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {invites.map((inv) => (
                    <tr key={inv.id} className="hover:bg-muted/40">
                      <td className="px-4 py-2">{inv.email}</td>
                      <td className="px-4 py-2 text-muted-foreground">
                        {new Date(inv.expiresAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          className="text-xs text-destructive hover:underline disabled:opacity-50"
                          onClick={() => cancelInviteMutation.mutate(inv.id)}
                          disabled={cancelInviteMutation.isPending}
                        >
                          Cancel
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Ownership transfer section */}
      <div className="mt-6">
        <h3 className="font-medium text-sm mb-3">Ownership transfer</h3>

        {pendingIncoming && (
          <div className="rounded-lg border border-border p-4 flex flex-col gap-3">
            <p className="text-sm">You have been offered ownership of this property.</p>

            {autoArchiveWarn && (
              <div className="rounded-md bg-yellow-50 border border-yellow-300 p-3 text-sm">
                <p className="font-medium text-yellow-800 mb-1">
                  Accepting will auto-archive {autoArchiveWarn.properties.length} of your{' '}
                  {autoArchiveWarn.properties.length === 1 ? 'property' : 'properties'}. Proceed?
                </p>
                <div className="flex gap-2 mt-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      acceptTransferMutation.mutate({
                        transferId: autoArchiveWarn.transferId,
                        confirmAutoArchive: true,
                      })
                    }
                    disabled={acceptTransferMutation.isPending}
                  >
                    Confirm
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setAutoArchiveWarn(null)}
                    disabled={acceptTransferMutation.isPending}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {!autoArchiveWarn && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() =>
                    acceptTransferMutation.mutate({ transferId: pendingIncoming.id })
                  }
                  disabled={
                    acceptTransferMutation.isPending || declineTransferMutation.isPending
                  }
                >
                  {acceptTransferMutation.isPending ? 'Accepting…' : 'Accept'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => declineTransferMutation.mutate(pendingIncoming.id)}
                  disabled={
                    acceptTransferMutation.isPending || declineTransferMutation.isPending
                  }
                >
                  {declineTransferMutation.isPending ? 'Declining…' : 'Decline'}
                </Button>
              </div>
            )}
          </div>
        )}

        {pendingOutgoing && !pendingIncoming && (
          <div className="rounded-lg border border-border p-4 flex items-center justify-between gap-4">
            <p className="text-sm">
              Ownership transfer pending to{' '}
              <span className="font-medium">
                {members.find((m) => m.userId === pendingOutgoing.toUserId)?.displayName ??
                  members.find((m) => m.userId === pendingOutgoing.toUserId)?.email ??
                  'another member'}
              </span>
              .
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => cancelTransferMutation.mutate(pendingOutgoing.id)}
              disabled={cancelTransferMutation.isPending}
            >
              {cancelTransferMutation.isPending ? 'Cancelling…' : 'Cancel'}
            </Button>
          </div>
        )}

        {isOwner && !pendingOutgoing && !pendingIncoming && (
          <div className="rounded-lg border border-border p-4 flex flex-col gap-3">
            {eligibleTransferTargets.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Add a member before transferring ownership.
              </p>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Transfer ownership to an existing member.
                </p>
                <div className="flex gap-2">
                  <select
                    value={transferTargetId}
                    onChange={(e) => { setTransferTargetId(e.target.value); setTransferError(null); }}
                    className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">Select member…</option>
                    {eligibleTransferTargets.map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.displayName} ({m.email})
                      </option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    onClick={() => initiateTransferMutation.mutate(transferTargetId)}
                    disabled={!transferTargetId || initiateTransferMutation.isPending}
                  >
                    {initiateTransferMutation.isPending ? 'Transferring…' : 'Transfer'}
                  </Button>
                </div>
                {transferError && <p className="text-sm text-destructive">{transferError}</p>}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
